import ShieldAlertIcon from "@redotech/redo-icons/alert-circle.svg";
import ArchiveIcon from "@redotech/redo-icons/archive.svg";
import CreditCardIcon from "@redotech/redo-icons/credit-card-01.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import BotMessageSquareIcon from "@redotech/redo-icons/message-chat-circle.svg";
import MessageSquareTextIcon from "@redotech/redo-icons/message-text-square-02.svg";
import PenLineIcon from "@redotech/redo-icons/pencil-01.svg";
import RadioIcon from "@redotech/redo-icons/radio.svg";
import RotateCcwIcon from "@redotech/redo-icons/reverse-left_filled.svg";
import BrainCircuitIcon from "@redotech/redo-icons/stars-01.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { classifyReturnsRouting } from "@redotech/redo-onboarding-api/returns-routing";
import type { ProductId } from "@redotech/redo-onboarding-api/schema/initialize";
import type { JSXElementConstructor } from "react";
import { getFormData, getProductConfig } from "./contexts/product-config-store";

declare const process: { env: Record<string, string | undefined> };

const S3_BASE =
  process.env.REDO_STATIC_ASSETS_URL ??
  "https://redoapi-dev.s3.amazonaws.com/redo/static/onboarding-v2";

type IconComponent = JSXElementConstructor<any>;

export interface ProductStep {
  id: string;
  label: string;
  path: string;
  shouldSkip?: () => boolean;
  hideFromNav?: boolean;
  navGroup?: string;
}

export interface ProductEntry {
  id: ProductId;
  label: string;
  subtext: string;
  /** When true the landing-page pill gets the animated glow/shimmer treatment */
  selfServe?: boolean;
  imageUrl: string;
  defaultRoute: string;
  calendarUrl?: string;
  /** When set, clicking this product on the landing page redirects to this path in the merchant app instead of opening the onboarding flow. */
  externalRedirectPath?: string;
  icon?: IconComponent;
  steps: ProductStep[];
}

export interface ProductFeatureFlags {
  omsOnboarding?: boolean;
  chargebacksOnboarding?: boolean;
  contextualOnboarding?: boolean;
  recoverOnboarding?: boolean;
}

const SCHEDULE_CALL_STEP: ProductStep = {
  id: "schedule-call",
  label: "Schedule a call",
  path: "schedule-call",
};

/**
 * True when the current merchant should be routed to the human-handoff path
 * (schedule-call) instead of self-serve go-live. See `classifyReturnsRouting`.
 */
export function isLargeReturnsMerchant(): boolean {
  return (
    classifyReturnsRouting(
      getProductConfig().dealSizing?.totalAnnualOrderVolume ?? null,
    ).decision === "call"
  );
}

export const PRODUCTS: ProductEntry[] = [
  {
    id: "returns",
    label: "Returns",
    subtext: "15 minute set up",
    selfServe: true,
    icon: RotateCcwIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-returns.webp`,
    defaultRoute: "overview",
    calendarUrl:
      "https://calendly.com/d/cw85-d74-6r6/redo-onboarding-support?primary_color=000000",
    steps: [
      {
        id: "ret-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "ret-return-policy",
        label: "Return Policy",
        path: "return-policy",
        navGroup: "Policy",
      },
      {
        id: "ret-return-options",
        label: "Return Options",
        path: "return-options",
        navGroup: "Policy",
      },
      {
        id: "ret-return-reasons",
        label: "Return Reasons",
        path: "return-reasons",
        navGroup: "Policy",
      },
      {
        id: "ret-return-address",
        label: "Return Address",
        path: "return-address",
        navGroup: "Policy",
      },
      {
        id: "ret-communications",
        label: "Communications",
        path: "communications",
        navGroup: "Communications",
      },
      {
        id: "ret-portal-preview",
        label: "Portal Preview",
        path: "portal-preview",
        navGroup: "Experience",
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-app-embed",
        label: "App Embed",
        path: "app-embed",
        navGroup: "Experience",
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-about-coverage",
        label: "Coverage",
        path: "about-coverage",
        navGroup: "Coverage",
        shouldSkip: () =>
          getProductConfig().adminConfig?.skipPricing === true ||
          isLargeReturnsMerchant(),
      },
      {
        id: "ret-coverage-widget",
        label: "Coverage Widget",
        path: "coverage-widget",
        navGroup: "Coverage",
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-billing",
        label: "Billing",
        path: "billing",
        navGroup: "Launch",
      },
      {
        id: "ret-go-live",
        label: "Go Live",
        path: "go-live",
        navGroup: "Launch",
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
        shouldSkip: isLargeReturnsMerchant,
      },
      {
        id: "ret-schedule-call",
        label: "Schedule a call",
        path: "schedule-call",
        navGroup: "Launch",
        shouldSkip: () => !isLargeReturnsMerchant(),
      },
    ],
  },
  {
    id: "order-editing",
    label: "Order Editing",
    subtext: "10 minute set up",
    selfServe: true,
    icon: PenLineIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-order-editing.webp`,
    defaultRoute: "overview",
    calendarUrl:
      "https://calendar.google.com/calendar/appointments/schedules/AcZssZ0waqwls1lUf-Gs6BpBE8qa3Ih0C5FJ2RS_6eEluatBFlIr9WAJGsRiY7A2OqFb-CQw4OHRiyhk?gv=true",
    steps: [
      {
        id: "oe-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      { id: "oe-how-it-works", label: "How It Works", path: "video-intro" },
      {
        id: "oe-customer-actions",
        label: "Customer Actions",
        path: "enabled-actions",
      },
      { id: "oe-timing", label: "Timing", path: "time-window" },
      {
        id: "oe-platforms",
        label: "Platforms",
        path: "platforms",
        shouldSkip: () => {
          const config = getProductConfig().orderEditing;
          if (!config) {
            return true;
          }
          return (
            !config.shopperAI &&
            !config.supportSelfServe &&
            !config.orderTracking
          );
        },
      },
      {
        id: "oe-widget-preview",
        label: "Widget Preview",
        path: "widget-preview",
      },
      { id: "oe-activate", label: "Activate", path: "app-embed" },
      {
        id: "oe-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "shipping-fulfillment",
    label: "Shipping & Fulfillment",
    subtext: "20 minute set up",
    selfServe: true,
    icon: TruckIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-shipping-fulfillment.webp`,
    defaultRoute: "overview",
    calendarUrl: "https://calendly.com/suz-getredo/30-minute-meeting",
    steps: [
      {
        id: "sf-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "sf-current-software",
        label: "Current Software",
        path: "current-software",
        navGroup: "Setup",
      },
      {
        id: "sf-channels",
        label: "Sales Channels",
        path: "channels",
        navGroup: "Setup",
      },
      {
        id: "sf-carriers",
        label: "Carriers",
        path: "carriers",
        navGroup: "Operations",
      },
      {
        id: "sf-packaging",
        label: "Packaging",
        path: "packaging",
        navGroup: "Operations",
      },
      {
        id: "sf-packing-slip",
        label: "Packing Slip",
        path: "packing-slip",
        navGroup: "Operations",
      },
      {
        id: "sf-shipping-automations",
        label: "Automations",
        path: "shipping-automations",
        navGroup: "Operations",
      },
      {
        id: "sf-order-automations",
        label: "Order Automations",
        path: "order-automations",
        navGroup: "Operations",
      },
      {
        id: "sf-international",
        label: "International",
        path: "international",
        navGroup: "Operations",
        shouldSkip: () =>
          getProductConfig().shippingFulfillment?.skipInternational === true,
      },
      {
        id: "sf-views",
        label: "Views",
        path: "views",
        navGroup: "Finalization",
      },
      {
        id: "sf-billing",
        label: "Billing",
        path: "billing",
        navGroup: "Finalization",
        shouldSkip: () =>
          getProductConfig().shippingFulfillment?.skipBilling === true,
      },
      {
        id: "sf-pain-points",
        label: "Feedback",
        path: "pain-points",
        navGroup: "Finalization",
      },
      {
        id: "sf-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "chargebacks",
    label: "Chargebacks",
    subtext: "5 minute set up",
    selfServe: true,
    icon: ShieldAlertIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-chargebacks.webp`,
    defaultRoute: "overview",
    calendarUrl: "https://calendly.com/cameron-buford-getredo/30min",
    steps: [
      {
        id: "cb-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "cb-how-it-works",
        label: "How It Works",
        path: "how-it-works",
        navGroup: "Overview",
      },
      {
        id: "cb-permissions",
        label: "Permissions",
        path: "permissions",
        navGroup: "Overview",
        shouldSkip: () =>
          getProductConfig().chargebacks?.hasDisputeScopes === true,
      },
      {
        id: "cb-dispute-sources",
        label: "Dispute Sources",
        path: "dispute-sources",
        navGroup: "Setup",
      },
      {
        id: "cb-go-live",
        label: "Go Live",
        path: "go-live",
        navGroup: "Launch",
      },
      {
        id: "cb-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "withdrawals",
    label: "Withdrawals",
    subtext: "5 minute set up",
    selfServe: true,
    icon: RotateCcwIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-returns.webp`,
    defaultRoute: SCHEDULE_CALL_STEP.path,
    externalRedirectPath: "/returns/withdrawals/all-withdrawals?setup=true",
    steps: [SCHEDULE_CALL_STEP],
  },
  {
    id: "recover",
    label: "Recover",
    subtext: "7 minute set up",
    selfServe: true,
    icon: MessageSquareTextIcon,
    imageUrl: `${S3_BASE}/recover.webp`,
    defaultRoute: "overview",
    calendarUrl:
      "https://calendly.com/d/cw85-d74-6r6/redo-onboarding-support?primary_color=000000",
    steps: [
      {
        id: "rec-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "rec-how-it-works",
        label: "How It Works",
        path: "how-it-works",
        navGroup: "Overview",
      },
      {
        id: "rec-business-info",
        label: "Business Info",
        path: "business-info",
        navGroup: "Setup",
      },
      {
        id: "rec-messaging",
        label: "Messaging",
        path: "messaging",
        navGroup: "Setup",
      },
      {
        id: "rec-billing",
        label: "Billing",
        path: "billing",
        navGroup: "Launch",
      },
      {
        id: "rec-go-live",
        label: "Go Live",
        path: "go-live",
        navGroup: "Launch",
      },
      {
        id: "rec-completion",
        label: "Complete",
        path: "completion",
        hideFromNav: true,
      },
    ],
  },
  {
    id: "contextual",
    label: "Contextual",
    subtext: "15 minute set up",
    selfServe: true,
    icon: RadioIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-contextual.webp`,
    defaultRoute: "overview",
    calendarUrl:
      "https://calendly.com/d/cw85-d74-6r6/redo-onboarding-support?primary_color=000000",
    steps: [
      {
        id: "ctx-overview",
        label: "Overview",
        path: "overview",
        hideFromNav: true,
      },
      {
        id: "ctx-subdomain",
        label: "Subdomain Setup",
        path: "subdomain",
        navGroup: "Setup",
      },
      {
        id: "ctx-tag-setup",
        label: "Tag Setup",
        path: "tag-setup",
        navGroup: "Setup",
        shouldSkip: () => {
          const fd = getFormData();
          const source = (fd["ctx-subdomain"] as any)?.merchantSource;
          const dests: string[] =
            (fd["ctx-destinations"] as any)?.selectedDestinations ?? [];
          const AD_PLATFORMS = ["meta", "tiktok", "snapchat"];
          return (
            source === "other" && !dests.some((d) => AD_PLATFORMS.includes(d))
          );
        },
      },
      {
        id: "ctx-destinations",
        label: "Destinations",
        path: "destinations",
        navGroup: "Integrations",
      },
      {
        id: "ctx-email-sms",
        label: "Email & SMS",
        path: "email-sms",
        navGroup: "Integrations",
        shouldSkip: () => {
          const dests: string[] =
            (getFormData()["ctx-destinations"] as any)?.selectedDestinations ??
            [];
          const EMAIL_SMS = ["klaviyo", "attentive", "postscript"];
          return !dests.some((d) => EMAIL_SMS.includes(d));
        },
      },
      {
        id: "ctx-ad-platforms",
        label: "Ad Platforms",
        path: "ad-platforms",
        navGroup: "Integrations",
        shouldSkip: () => {
          const dests: string[] =
            (getFormData()["ctx-destinations"] as any)?.selectedDestinations ??
            [];
          const AD_PLATFORMS = ["meta", "tiktok", "snapchat"];
          return !dests.some((d) => AD_PLATFORMS.includes(d));
        },
      },
      {
        id: "ctx-completion",
        label: "Complete",
        path: "completion",
        navGroup: "Launch",
      },
    ],
  },
  {
    id: "email-sms",
    label: "Email & SMS",
    subtext: "Schedule a call",
    icon: MailIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-email-sms.webp`,
    defaultRoute: "schedule-call",
    calendarUrl:
      "https://meetings.hubspot.com/bthurgood/emailsms-round-robin?embed=true",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "aeo",
    label: "Answer Engine Optimization",
    subtext: "Schedule a call",
    icon: BrainCircuitIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-aeo.webp`,
    defaultRoute: "schedule-call",
    calendarUrl:
      "https://calendly.com/jake-bengtzen-getredo/aeo-onboarding-call-jake-with-redo",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "checkout-optimization",
    label: "Checkout Optimization",
    subtext: "Schedule a call",
    icon: CreditCardIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-checkout-optimization.webp`,
    defaultRoute: "schedule-call",
    calendarUrl: "https://calendly.com/cameron-buford-getredo/30min",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "ai-sales-support",
    label: "AI Sales + Support",
    subtext: "Schedule a call",
    icon: BotMessageSquareIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-ai-sales-support.webp`,
    defaultRoute: "schedule-call",
    calendarUrl:
      "https://calendar.google.com/calendar/appointments/schedules/AcZssZ1_9_C99TyeDJSjHbpR66raLI6IadNvl9260l8EW0_CpUO3QM0YUP20rA7a8UsnNhpok1NG4Hpq?gv=true",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
  {
    id: "inventory-management",
    label: "Inventory Management",
    subtext: "Schedule a call",
    icon: ArchiveIcon,
    imageUrl: `${S3_BASE}/onboarding-v2-inventory-management.webp`,
    defaultRoute: "schedule-call",
    calendarUrl: "https://calendar.app.google/SWtX4oB5QeSjLuM47",
    steps: [
      { id: "schedule-call", label: "Schedule a call", path: "schedule-call" },
    ],
  },
];

export const PRODUCTS_BY_ID = new Map(PRODUCTS.map((p) => [p.id, p]));

function downgradeToCallOnly(product: ProductEntry): ProductEntry {
  return {
    ...product,
    subtext: "Schedule a call",
    selfServe: false,
    defaultRoute: SCHEDULE_CALL_STEP.path,
    steps: [SCHEDULE_CALL_STEP],
  };
}

export function getProductsForFeatureFlags(
  featureFlags?: ProductFeatureFlags | null,
): ProductEntry[] {
  return PRODUCTS.map((product) => {
    if (
      product.id === "shipping-fulfillment" &&
      featureFlags?.omsOnboarding === false
    ) {
      return downgradeToCallOnly(product);
    }
    if (product.id === "chargebacks") {
      return {
        ...product,
        subtext: "10 minute set up",
        selfServe: true,
        externalRedirectPath:
          "/payments/chargebacks/overview?setupSource=setup_app",
        defaultRoute: SCHEDULE_CALL_STEP.path,
        steps: [SCHEDULE_CALL_STEP],
      };
    }
    if (
      product.id === "contextual" &&
      featureFlags?.contextualOnboarding === false
    ) {
      return downgradeToCallOnly(product);
    }
    if (product.id === "recover" && featureFlags?.recoverOnboarding === false) {
      return downgradeToCallOnly(product);
    }
    return product;
  });
}
